import {
  DndContext,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  PointerSensor,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export const SortableGrid = ({
  children,
  items,
  onDragEnd,
}: {
  children: React.ReactNode;
  items: { id: any; [P: string]: any }[];
  onDragEnd: (newItems: Record<string, any>[]) => void;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor)
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over?.id);

      if (oldIndex !== newIndex) {
        onDragEnd(arrayMove(items, oldIndex, newIndex));
      }
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items}>{children}</SortableContext>
    </DndContext>
  );
};

export const SortableGridItem = ({
  id,
  children,
  disabled,
  hidden = false,
}: {
  id: string;
  children: React.ReactNode | ((isDragging: boolean) => React.ReactNode);
  disabled?: boolean;
  hidden?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const [isdown, setIsDown] = useState(false);

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 1 : 0,
      opacity: isDragging ? 0.5 : 1,
      cursor: isDragging || (isdown && !disabled) ? 'grabbing' : disabled ? 'inherit' : 'grab',
    }),
    [disabled, isDragging, isdown, transform, transition]
  );

  return (
    <div
      ref={setNodeRef}
      onMouseDown={() => setIsDown(true)}
      onMouseUp={() => setIsDown(false)}
      style={style}
      hidden={hidden}
      {...attributes}
      {...listeners}
    >
      {typeof children === 'function' ? children(isDragging) : children}
    </div>
  );
};
